import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../services/api';

const POLL_MS = 1000;
const MAX_POLL_FAILURES = 5;

/**
 * Disqualify Alchemer responses and follow the job until it's finished: Alchemer, its quota
 * refresh, and telling Wrangler (7j). run(ids) resolves with the result, or null on failure.
 */
const useDisqualify = (surveyId, { pollMs = POLL_MS } = {}) => {
  const [state, setState] = useState({ running: false, progress: 0, total: 0, result: null, error: null });
  const timer = useRef(null);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; clearTimeout(timer.current); };
  }, []);

  const run = useCallback(async (ids) => {
    clearTimeout(timer.current);
    setState({ running: true, progress: 0, total: ids.length, result: null, error: null });
    try {
      await api.post(`/surveys/${surveyId}/disqualify-duplicates/`, { ids });
    } catch (err) {
      setState((s) => ({ ...s, running: false, error: err?.response?.data?.error || "Couldn't start the disqualification." }));
      return null;
    }
    return new Promise((resolve) => {
      let failures = 0;
      const poll = async () => {
        if (!alive.current) return resolve(null);
        try {
          const { data } = await api.get(`/surveys/${surveyId}/disqualify-progress/`);
          failures = 0;
          if (data.done) {
            setState({ running: false, progress: data.total, total: data.total, result: data.result, error: null });
            return resolve(data.result);
          }
          setState((s) => ({ ...s, progress: data.progress, total: data.total || s.total }));
        } catch {
          failures += 1;
          if (failures >= MAX_POLL_FAILURES) {
            setState((s) => ({ ...s, running: false, error: "Lost track of the disqualification. It may still be running; check again shortly." }));
            return resolve(null);
          }
        }
        timer.current = setTimeout(poll, pollMs);
        return undefined;
      };
      timer.current = setTimeout(poll, pollMs);
    });
  }, [surveyId, pollMs]);

  return { ...state, run };
};

export default useDisqualify;
